const _state = {
  // "The slide selected in the PowerPoint EDITOR is this frame's" — true / false / null.
  //
  // THREE-VALUED, not a boolean. null is "nobody could tell", which is a different fact from "this
  // slide is provably not mine": the host may lack the tag API, the slide may carry no AhaSlides
  // binding, or the read may have timed out. services/office/current-slide.js keeps the same three
  // outcomes apart for the reason its SLIDE_OWNERSHIP comment gives — collapsing them forces one
  // caller to be wrong — and a consumer that treats null as false should be choosing that visibly.
  //
  // Deliberately NOT folded into the root's pptSlideLostFocus. That flag is a LATCH consumed by a
  // re-assert inside a live show, and isPptFrameStoodDown's comment records what its lifetime
  // depends on (including the addinPPTActiveView === 'read' term that stops it leaking into the
  // editor). This is a plain level with no role in the show, cleared by different things, so it gets
  // its own key rather than sharing one whose lifetime it does not obey.
  //
  // Written only by services/office/current-slide-edit-mode.js, via the wiring in
  // services/office/index.js. Holds its initial value on every non-add-in host, where nothing ever
  // commits it — harmless there, since isPptEditSlideNotSelected's isEnableAddinPowerpoint term has
  // already made the whole getter false.
  //
  // Starts TRUE — "assume this frame's slide is the selected one until the host says otherwise" —
  // which is the opposite of how isPptEditSlideNotSelected treats every LATER unknown.
  //
  // The initial value is not a verdict, it is the absence of one, and the two want opposite
  // defaults. current-slide-edit-mode.js resolves its baseline in one host round trip, but the view
  // resolves to 'edit' before that returns, so a null start makes every frame — including the single
  // frame sitting on the slide the user is looking at — read as not-selected for that stretch.
  //
  // Being wrong for that window is cheap in one direction and not the other. Wrong-and-playing costs
  // a moment of audio from a frame that turns out not to be selected, corrected the instant the
  // baseline lands. Wrong-and-silent does NOT correct itself: pausing an <audio> clears its
  // can-autoplay flag (see resumeAudioOnPptStandUpAction), so the element cannot restart when the
  // verdict arrives, and the resume has to go through a script play() that the autoplay policy
  // blocks until something is clicked inside the frame. That frame stays silent for the session.
  //
  // Still THREE-VALUED once the host starts answering. null keeps meaning "asked, nobody could
  // tell", and isPptEditSlideNotSelected keeps failing closed on it for the reason its own comment
  // gives. Only the pre-answer default is opinionated.
  editSelectedSlideIsMine: true,

  // AHA-47221: which slides the user has ticked in the editor sidebar, to be taken into
  // PowerPoint. Ids, not indices — the sidebar reorders, duplicates and deletes slides while
  // a selection is open, and an index would silently come to mean a different slide.
  //
  // Lives in the store rather than in SideLeft's data because the tick boxes render inside
  // ToolBoxSideLeft (one component per row) while whatever acts on the selection sits
  // elsewhere in the editor chrome; a shared list is the only thing both can see.
  //
  // Empty on every non-add-in host, where nothing renders a box to tick.
  selectedSlideIds: [],

  // AHA-47221: the presentation the ids above were ticked in.
  //
  // The selection is per-presentation; this module is not. One store serves the whole task pane,
  // and the add-in's editor is left and re-entered for a DIFFERENT presentation without the page
  // ever reloading — back out to the presentation list, pick another one. Nothing in that
  // sequence touches the ids, so without an owner recorded here they simply stay: the next deck
  // opens with a count in the Add bar and no ticked box anywhere in the sidebar to account for
  // it, because the ids belong to a presentation that is no longer on screen. Worse than the
  // miscount, the default tick then declines to run (it only fills a selection nobody has
  // touched), so Add would put the previous presentation's slide into the deck.
  //
  // Kept here beside the ids rather than read off state.presentation.id at use time, so that the
  // reset happens once, where the selection is, instead of every consumer having to remember to
  // ask whose ids these are. Committed by PptEditorMixin, which watches the editor's
  // presentation; see setPptSelectionPresentationId for what a change does.
  //
  // null until an add-in editor mounts, which is also its value on every host where nothing
  // renders a tick box.
  selectedSlideIdsPresentationId: null,
}

export default _state
